import type { ConsentGate, ConsentState } from '@highereducation/mfe-loader';
import { logger } from '@hen-site/lib/logger/client';
import {
  MARKETING_CONSENT_POLL_INTERVAL_MS,
  MARKETING_CONSENT_POLL_MAX_ATTEMPTS,
  ONETRUST_GROUP,
  ONETRUST_GROUPS_UPDATED_EVENT,
} from '@hen-site/lib/paid-social/constants';

/**
 * The standard OneTrust group id for each consent category. A category that
 * is not listed has no group, so it is never granted.
 */
export const CONSENT_CATEGORY_GROUPS: Readonly<Record<string, string>> = {
  'strictly-necessary': ONETRUST_GROUP.NECESSARY,
  performance: ONETRUST_GROUP.PERFORMANCE,
  functional: ONETRUST_GROUP.FUNCTIONAL,
  advertising: ONETRUST_GROUP.ADVERTISING,
};

function activeGroups(): string[] | undefined {
  if (
    typeof window === 'undefined' ||
    typeof window.OnetrustActiveGroups !== 'string'
  ) {
    return undefined;
  }
  return window.OnetrustActiveGroups.split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

/**
 * A consent gate over OneTrust's active groups. Matching is token-exact, so
 * one group id cannot match as a substring of another. Until OneTrust exposes
 * its groups the answer is `unknown`. If it does not appear within the poll
 * budget the answer becomes `denied`, and it is reconsidered whenever OneTrust
 * reports a change.
 */
export function createOneTrustConsent(): ConsentGate {
  const listeners = new Set<() => void>();
  let attempts = 0;
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const notify = (): void => {
    for (const listener of Array.from(listeners)) {
      listener();
    }
  };

  const poll = (): void => {
    timer = undefined;
    if (listeners.size === 0) {
      return;
    }
    if (activeGroups() !== undefined) {
      notify();
      return;
    }
    if (attempts >= MARKETING_CONSENT_POLL_MAX_ATTEMPTS) {
      timedOut = true;
      logger.warn('Consent signal did not appear before poll timeout');
      notify();
      return;
    }
    attempts += 1;
    timer = setTimeout(poll, MARKETING_CONSENT_POLL_INTERVAL_MS);
  };

  return {
    read(category): ConsentState {
      const group = Object.prototype.hasOwnProperty.call(
        CONSENT_CATEGORY_GROUPS,
        category,
      )
        ? CONSENT_CATEGORY_GROUPS[category]
        : undefined;
      if (!group) {
        return 'denied';
      }
      const active = activeGroups();
      if (active === undefined) {
        return timedOut ? 'denied' : 'unknown';
      }
      return active.includes(group) ? 'granted' : 'denied';
    },

    subscribe(_category, onChange) {
      if (typeof window === 'undefined') {
        return () => undefined;
      }
      if (listeners.size === 0) {
        window.addEventListener(ONETRUST_GROUPS_UPDATED_EVENT, notify);
      }
      listeners.add(onChange);
      if (timer === undefined && !timedOut && activeGroups() === undefined) {
        poll();
      }
      return () => {
        listeners.delete(onChange);
        if (listeners.size === 0) {
          window.removeEventListener(ONETRUST_GROUPS_UPDATED_EVENT, notify);
          if (timer !== undefined) {
            clearTimeout(timer);
            timer = undefined;
          }
        }
      };
    },
  };
}
